import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { Calendar, TrendingUp, Users, Building2, DollarSign, Filter } from 'lucide-react';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface AnalyticsData {
  trafficData: any[];
  demographicsData: any[];
  competitionData: any[];
  revenueData: any[];
}

const AnalyticsPage: React.FC = () => {
  const [dateRange, setDateRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    trafficData: [],
    demographicsData: [],
    competitionData: [],
    revenueData: []
  });

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // In a real app, this would fetch from your analytics API
      // For now, we'll generate sample data
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      
      const trafficData = generateTimeSeriesData(days, 'Traffic');
      const demographicsData = generateDemographicsData();
      const competitionData = generateCompetitionData();
      const revenueData = generateTimeSeriesData(days, 'Revenue');

      setAnalyticsData({
        trafficData,
        demographicsData,
        competitionData,
        revenueData
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateTimeSeriesData = (days: number, type: string) => {
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - i - 1));
      return {
        date: format(date, 'MMM dd'),
        value: Math.floor(Math.random() * 1000) + 500,
        trend: Math.floor(Math.random() * 100) + 50
      };
    });
  };

  const generateDemographicsData = () => {
    return [
      { age: '18-24', percentage: 15 },
      { age: '25-34', percentage: 30 },
      { age: '35-44', percentage: 25 },
      { age: '45-54', percentage: 20 },
      { age: '55+', percentage: 10 }
    ];
  };

  const generateCompetitionData = () => {
    return [
      { type: 'Direct', count: 12 },
      { type: 'Indirect', count: 18 },
      { type: 'Potential', count: 8 }
    ];
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-primary">Analytics Dashboard</h1>
        
        <div className="flex items-center space-x-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-card border border-border rounded-lg px-4 py-2"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          
          <button className="flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg">
            <Filter size={20} />
            <span>Filters</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Foot Traffic Chart */}
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center">
            <Users className="mr-2 text-primary" />
            Foot Traffic Trends
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.trafficData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#2563eb" name="Daily Traffic" />
              <Line type="monotone" dataKey="trend" stroke="#16a34a" name="Trend" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Demographics Chart */}
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center">
            <Users className="mr-2 text-primary" />
            Demographics Distribution
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.demographicsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="percentage" fill="#2563eb" name="Percentage" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Competition Analysis */}
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center">
            <Building2 className="mr-2 text-primary" />
            Competition Analysis
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.competitionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#16a34a" name="Number of Businesses" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Potential */}
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center">
            <DollarSign className="mr-2 text-primary" />
            Revenue Potential
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#dc2626" name="Projected Revenue" />
              <Line type="monotone" dataKey="trend" stroke="#9333ea" name="Market Trend" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-6">Key Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <h3 className="font-medium">Traffic Patterns</h3>
            <p className="text-sm text-muted-foreground">
              Peak hours show consistent growth with 23% increase in foot traffic during weekends.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Market Position</h3>
            <p className="text-sm text-muted-foreground">
              Currently in a growth phase with moderate competition and strong potential for market leadership.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Growth Opportunities</h3>
            <p className="text-sm text-muted-foreground">
              Expanding demographic reach could increase market share by an estimated 15%.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;